import React, { useState, useEffect } from 'react';
import { TextInput, Box, Container, Title, Button } from '@mantine/core';
import { useSocket } from '../contexts/SocketContext';
import { useTheme } from '../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';

export function ThemedDataCollection({ instance }) {
  const socket = useSocket();
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [isSpinning, setIsSpinning] = useState(false);
  const [triangles, setTriangles] = useState([]);

  // Generate color palette from theme
  const themeColors = [
    theme.colors.primary,
    theme.colors.secondary,
    theme.colors.accent,
    // Generate variations
    theme.colors.primary + '99', // Add transparency
    theme.colors.secondary + 'BB',
    theme.colors.accent + 'DD'
  ];

  useEffect(() => {
    if (socket) {
      console.log(`${theme.name} Socket connected:`, socket.connected);
      
      socket.on('connect', () => {
        console.log(`${theme.name} Socket connected`);
        socket.emit('test_event', { message: `Hello from ${theme.name} client` });
      });

      socket.on('disconnect', () => {
        console.log(`${theme.name} Socket disconnected`);
      });

      socket.on('connect_error', (error) => {
        console.error(`${theme.name} Socket connection error:`, error);
      });

      socket.on('test_response', (data) => {
        console.log(`Received ${theme.name} test response:`, data);
      });
    } else {
      console.log(`${theme.name} Socket not available`);
    }
  }, [socket, theme.name]);

  useEffect(() => {
    if (isSpinning) {
      const interval = setInterval(() => {
        const newTriangle = {
          id: Math.random(),
          x: Math.random() * window.innerWidth,
          y: window.innerHeight + 50,
          color: themeColors[Math.floor(Math.random() * themeColors.length)],
          size: Math.random() * 30 + 20,
          rotation: Math.random() * 360,
          velocity: Math.random() * 3 + 2
        };
        
        setTriangles(prev => [...prev, newTriangle]);
      }, 200);

      const cleanupInterval = setInterval(() => {
        setTriangles(prev => prev.filter(triangle => triangle.y > -100));
      }, 1000);

      return () => {
        clearInterval(interval);
        clearInterval(cleanupInterval);
      };
    }
  }, [isSpinning, themeColors]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email && socket) {
      console.log(`Submitting ${theme.name} entry:`, email);
      setIsSpinning(true);
      socket.emit('spin_wheel', { email, instance });
      
      setTimeout(() => {
        setIsSpinning(false);
        setTriangles([]);
        setEmail('');
      }, 5000);
    }
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  return (
    <Box style={{ 
      minHeight: '100vh', 
      background: theme.colors.background,
      position: 'relative',
      overflow: 'hidden'
    }}>
      <AnimatePresence>
        {triangles.map((triangle) => (
          <motion.div
            key={triangle.id}
            initial={{ 
              x: triangle.x, 
              y: triangle.y,
              rotate: triangle.rotation,
              scale: 0
            }}
            animate={{ 
              y: triangle.y - window.innerHeight - 200,
              rotate: triangle.rotation + 360,
              scale: 1
            }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ 
              duration: triangle.velocity,
              ease: "linear"
            }}
            style={{
              position: 'absolute',
              width: 0,
              height: 0,
              borderLeft: `${triangle.size/2}px solid transparent`,
              borderRight: `${triangle.size/2}px solid transparent`,
              borderBottom: `${triangle.size}px solid ${triangle.color}`,
              zIndex: 1
            }}
          />
        ))}
      </AnimatePresence>

      <Container 
        size="sm" 
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          position: 'relative',
          zIndex: 10
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{ textAlign: 'center', marginBottom: '2rem' }}
        >
          {theme.logo && theme.logo.startsWith('/') ? (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <img 
                src={theme.logo} 
                alt={theme.logoAlt || `${theme.name} Logo`} 
                style={{ 
                  height: '160px'
                }} 
              />
            </div>
          ) : (
            <Title 
              order={1} 
              style={{ 
                color: 'white', 
                fontSize: '3rem', 
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                marginBottom: '1rem'
              }}
            >
              {theme.logo} {theme.name}
            </Title>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            padding: '2rem',
            borderRadius: '20px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            width: '100%',
            maxWidth: '400px'
          }}
        >
          <form onSubmit={handleSubmit}>
            <TextInput
              label="Enter your email to spin the wheel"
              placeholder="your.email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              size="lg"
              style={{ marginBottom: '1.5rem' }}
              styles={{
                label: {
                  color: theme.colors.secondary,
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  marginBottom: '0.5rem'
                },
                input: {
                  borderRadius: '12px',
                  border: '2px solid #e5e7eb',
                  fontSize: '1rem',
                  padding: '12px 16px',
                  '&:focus': {
                    borderColor: theme.colors.primary,
                    boxShadow: `0 0 0 3px ${theme.colors.primary}20`
                  }
                }
              }}
            />
            
            <Button
              type="submit"
              size="lg"
              fullWidth
              disabled={!isValidEmail(email) || isSpinning}
              loading={isSpinning}
              style={{
                background: isSpinning 
                  ? `linear-gradient(45deg, ${theme.colors.accent}, ${theme.colors.primary})` 
                  : `linear-gradient(45deg, ${theme.colors.primary}, ${theme.colors.secondary})`,
                border: 'none',
                borderRadius: '12px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                height: '50px',
                transition: 'all 0.3s ease',
                transform: isSpinning ? 'scale(0.98)' : 'scale(1)',
                boxShadow: isSpinning 
                  ? `0 4px 15px ${theme.colors.primary}66` 
                  : `0 8px 25px ${theme.colors.primary}50`
              }}
            >
              {isSpinning ? '🎲 Spinning...' : '🎯 Spin the Wheel!'}
            </Button>
          </form>

          {isSpinning && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                textAlign: 'center',
                marginTop: '1rem',
                color: theme.colors.primary,
                fontWeight: 'bold'
              }}
            >
              Good luck! 🍀
            </motion.div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          style={{
            marginTop: '2rem',
            textAlign: 'center',
            color: theme.colors.light || 'rgba(255,255,255,0.9)',
            fontSize: '0.9rem'
          }}
        >
          {theme.mascot && (
            <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img 
                src={theme.mascot} 
                alt={theme.mascotAlt || 'Mascot'} 
                style={{ 
                  width: '40px', 
                  height: '40px',
                  borderRadius: '50%',
                  marginRight: '0.5rem',
                  opacity: 0.8
                }} 
              />
              <span>Charlie is rooting for you!</span>
            </div>
          )}
        </motion.div>
      </Container>
    </Box>
  );
}
